import { useState, useEffect, useRef, useCallback } from "react";

const API = "/api-optimizer";

// ── Profiles ─────────────────────────────────────────────
const RISK_PROFILES = {
  prudent:     { sl:.02, tp:.03, trailing:null, label:"Prudent",     emoji:"🐣", color:"#1A7FB5", bg:"#EBF5FF" },
  neutre:      { sl:.04, tp:.06, trailing:null, label:"Neutre",      emoji:"⚖️",  color:"#2D6EE8", bg:"#EBF2FF" },
  agressif:    { sl:.06, tp:.12, trailing:null, label:"Agressif",    emoji:"🚀", color:"#E07800", bg:"#FFF5EB" },
  asymetrique: { sl:.03, tp:.09, trailing:null, label:"Asymétrique", emoji:"🎯", color:"#7B5CF5", bg:"#F5F0FF" },
  tendance:    { sl:.02, tp:null,trailing:.05,  label:"Tendance",    emoji:"📈", color:"#00A870", bg:"#F0FDF8" },
};
const BOT_COUNTS   = [25, 50, 75, 100];
const DURATIONS    = [
  { label:"5 minutes", bars:576, interval:"5m", days:2   },
  { label:"1 heure",   bars:336, interval:"1h", days:14  },
  { label:"24 heures", bars:90,  interval:"1d", days:90  },
  { label:"7 jours",   bars:52,  interval:"1w", days:364 },
  { label:"30 jours",  bars:23,  interval:"1M", days:720 },
];
const STAKES       = [1, 10, 100];
const PROFILE_KEYS = Object.keys(RISK_PROFILES);
const SYMBOLS      = ["BTCUSDT","ETHUSDT","BNBUSDT","XRPUSDT","SOLUSDT","ADAUSDT","DOGEUSDT","AVAXUSDT","DOTUSDT","MATICUSDT"];
const TOTAL_COMBOS = BOT_COUNTS.length * DURATIONS.length * STAKES.length * PROFILE_KEYS.length;

// ── API ───────────────────────────────────────────────────
const api = {
  async getKlines(symbol, interval="1h", days=90) {
    const r = await fetch(`${API}/klines/${symbol}?interval=${interval}&days=${days}`);
    if (!r.ok) throw new Error("klines error");
    const { data } = await r.json();
    return data;
  },
  async saveBacktests(results) {
    await fetch(`${API}/backtests`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({results}) });
  },
  async loadLatestBacktests() {
    const r = await fetch(`${API}/backtests/latest`); return r.ok ? r.json() : [];
  },
  async getSims() {
    const r = await fetch(`${API}/simulations`); return r.ok ? r.json() : [];
  },
  async createSim(data) {
    const r = await fetch(`${API}/simulations`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(data) });
    return r.ok ? r.json() : null;
  },
  async updateSim(id, data) {
    await fetch(`${API}/simulations/${id}`, { method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify(data) }).catch(()=>{});
  },
  async deleteSim(id) {
    await fetch(`${API}/simulations/${id}`, { method:"DELETE" }).catch(()=>{});
  },
  async saveBestConfig(config) {
    await fetch(`${API}/best-config`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({config}) });
  },
  async saveTrade(t) {
    await fetch(`${API}/trades`, { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(t) }).catch(()=>{});
  },
  async health() {
    const r = await fetch(`${API}/health`).catch(()=>null); return r?.ok ? r.json() : null;
  },
};

// ── Indicateurs ───────────────────────────────────────────
function calcRSI(c,p=14){if(c.length<p+1)return 50;let g=0,l=0;for(let i=c.length-p;i<c.length;i++){const d=c[i]-c[i-1];if(d>0)g+=d;else l-=d;}return 100-100/(1+g/(l||1e-9));}
function calcEMA(c,p){if(c.length<p)return c.at(-1);const k=2/(p+1);let e=c.slice(0,p).reduce((a,b)=>a+b,0)/p;for(let i=p;i<c.length;i++)e=c[i]*k+e*(1-k);return e;}
function calcBB(c,p=20){if(c.length<p)return{upper:Infinity,lower:-Infinity};const s=c.slice(-p),m=s.reduce((a,b)=>a+b,0)/p,std=Math.sqrt(s.reduce((a,v)=>a+(v-m)**2,0)/p);return{upper:m+2*std,lower:m-2*std};}
function calcAvgVol(bars,p=20){if(bars.length<p)return 0;return bars.slice(-p).reduce((s,b)=>s+(b.vol||0),0)/p;}

// getSignal now takes full bar objects to access volume + EMA trend filter
function getSignal(bars){
  if(bars.length<52) return "HOLD";
  const closes = bars.map(b=>b.close);
  const vols   = bars.map(b=>b.vol||0);
  const rsi    = calcRSI(closes);
  const macd   = calcEMA(closes,12) - calcEMA(closes,26);
  const bb     = calcBB(closes);
  const p      = closes.at(-1), pp=closes.at(-2);
  const ema50  = calcEMA(closes.slice(-52), 50);
  const avgVol = calcAvgVol(bars);
  const volOk  = avgVol>0 ? (vols.at(-1)||0) > avgVol*1.1 : true;
  const upTrend = p > ema50;

  let bu=0, be=0;
  if(rsi<35) bu++; if(rsi>65) be++;
  if(macd>0) bu++; if(macd<0) be++;
  if(p<bb.lower) bu++; if(p>bb.upper) be++;
  if(p>pp) bu++; if(p<pp) be++;

  // EMA trend filter: only trade in trend direction
  // Volume filter: require above-average volume to confirm entry
  const buyOk  = bu>=3 && upTrend && volOk;
  const sellOk = be>=3 && !upTrend;
  return buyOk?"BUY":sellOk?"SELL":"HOLD";
}

// ── Core backtest loop (single slice) ─────────────────────
function _backtestSlice({slice, stake, sl, tp, trailing, trackEquity=false}){
  let cap=stake, peak=stake, inPos=false, entry=0, highWater=0, prevRSI=50, prevMACD=0;
  let tt=0, tw=0, mdd=0;
  const equity=trackEquity?[]:null;
  for(let i=51; i<slice.length; i++){
    const window = slice.slice(Math.max(0,i-80), i+1);
    const closes = window.map(c=>c.close);
    const price  = slice[i].close;
    const sig    = getSignal(window);
    const rsi    = calcRSI(closes);
    const macd   = calcEMA(closes,12) - calcEMA(closes,26);
    if(!inPos && sig==="BUY"){
      inPos=true; entry=price; highWater=price; cap*=.999;
      prevRSI=rsi; prevMACD=macd;
    } else if(inPos){
      if(price>highWater) highWater=price;
      const hitSL    = price <= entry*(1-sl);
      const hitTP    = tp && price >= entry*(1+tp);
      const hitTrail = trailing && price <= highWater*(1-trailing);
      const rsiRev   = trailing && prevRSI>=70 && rsi<65;
      const macdRev  = trailing && prevMACD>=0 && macd<0;
      if(hitSL||hitTP||hitTrail||rsiRev||macdRev){
        cap += cap*((price-entry)/entry); cap*=.999;
        if(price>entry) tw++;
        tt++; inPos=false; highWater=0;
        if(cap>peak) peak=cap;
        const dd=(peak-cap)/peak; if(dd>mdd) mdd=dd;
      }
      prevRSI=rsi; prevMACD=macd;
    }
    if(equity) equity.push(inPos ? cap*(1+(price-entry)/entry) : cap);
  }
  return{pnl:cap-stake, tt, tw, mdd, equity};
}

// ── Walk-forward validation (3 folds) ────────────────────
function walkForward({slice, stake, sl, tp, trailing}){
  if(slice.length < 60) return {wfScore:null, wfFolds:[]};
  const n=slice.length, folds=3;
  const foldSize=Math.floor(n/folds);
  const results=[];
  for(let f=0;f<folds;f++){
    // in-sample: all data up to 70% of this fold's end
    const foldEnd  = Math.min((f+1)*foldSize, n);
    const isEnd    = Math.floor(foldEnd*0.7);
    const ooSlice  = slice.slice(isEnd, foldEnd);
    if(ooSlice.length<60) continue;
    const r = _backtestSlice({slice:ooSlice, stake, sl, tp, trailing});
    results.push({roi:r.tt>0?(r.pnl/stake)*100:0, trades:r.tt, winRate:r.tt>0?(r.tw/r.tt)*100:0});
  }
  const wfScore = results.length>0 ? results.reduce((s,r)=>s+r.roi,0)/results.length : null;
  return{wfScore, wfFolds:results};
}

// ── Backtest (fidèle au bot réel : trailing, RSI rev, MACD rev) ──
function runBacktest({hist, numBots, bars, stake, profile, withEquity=false}){
  const {sl, tp, trailing} = RISK_PROFILES[profile];
  const bots = Math.min(numBots, 15);
  let totalPnl=0, tt=0, tw=0, mdd=0;
  const maxBars = Math.min(bars, Math.max(...SYMBOLS.slice(0,bots).map(s=>(hist[s]||[]).length)));
  const equityByBar = withEquity ? new Array(maxBars).fill(0) : null;

  for(let b=0; b<bots; b++){
    const sym   = SYMBOLS[b % SYMBOLS.length];
    const slice = (hist[sym]||[]).slice(-Math.min(bars, (hist[sym]||[]).length));
    const r = _backtestSlice({slice, stake, sl, tp, trailing, trackEquity:!!withEquity});
    totalPnl += r.pnl; tt += r.tt; tw += r.tw; if(r.mdd>mdd) mdd=r.mdd;
    if(equityByBar && r.equity){
      r.equity.forEach((v,i)=>{ if(i<equityByBar.length) equityByBar[i]+=v; });
    }
  }
  const scale = numBots/bots;
  let equity = null;
  if(withEquity && equityByBar){
    const raw = equityByBar.map(v=>v*scale);
    const step = Math.max(1, Math.floor(raw.length/200));
    equity = raw.filter((_,i)=>i%step===0);
  }
  // walk-forward on first bot symbol as representative
  const wfSym = SYMBOLS[0];
  const wfSlice = (hist[wfSym]||[]).slice(-Math.min(bars,(hist[wfSym]||[]).length));
  const {wfScore, wfFolds} = walkForward({slice:wfSlice, stake, sl, tp, trailing});
  return{ pnl:totalPnl*scale, roi:((totalPnl*scale)/(stake*numBots))*100, winRate:tt>0?(tw/tt)*100:0, trades:Math.round(tt*scale), drawdown:mdd*100, equity, wfScore, wfFolds };
}

// ── UI helpers ────────────────────────────────────────────
function ProfileBadge({profile}){
  const p=RISK_PROFILES[profile]; if(!p) return null;
  return <span style={{background:p.bg,color:p.color,border:`1px solid ${p.color}33`,borderRadius:5,padding:"2px 10px",fontSize:10,fontWeight:700}}>{p.emoji} {p.label}</span>;
}
function SigBadge({sig}){
  const c=sig==="BUY"?"#00A870":sig==="SELL"?"#E03030":"#E07800";
  return <span style={{background:c+"18",color:c,border:`1px solid ${c}33`,borderRadius:4,padding:"2px 8px",fontSize:10,fontWeight:700}}>{sig}</span>;
}
function MiniSpark({data=[],color="#2D6EE8",w=120,h=32}){
  const vals=data.filter(v=>typeof v==="number"&&!isNaN(v));
  if(vals.length<2) return <div style={{width:w,height:h,background:"#F0F2FA",borderRadius:4}}/>;
  const mn=Math.min(...vals),mx=Math.max(...vals),rng=mx-mn||.01;
  const pts=vals.map((v,i)=>`${((i/(vals.length-1))*w).toFixed(1)},${(h-((v-mn)/rng)*h).toFixed(1)}`).join(" ");
  return(
    <svg width={w} height={h}>
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill={color+"22"}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
    </svg>
  );
}
function StatCard({label,value,color="#1A1D2E",sub}){
  return(
    <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:10,padding:"14px 18px",boxShadow:"0 2px 6px rgba(0,0,0,0.04)"}}>
      <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:3,marginBottom:5,textTransform:"uppercase"}}>{label}</div>
      <div style={{fontSize:20,fontWeight:700,color,fontFamily:"IBM Plex Mono"}}>{value}</div>
      {sub&&<div style={{fontSize:9,color:"#AAB0C8",marginTop:2}}>{sub}</div>}
    </div>
  );
}

const BTN = {
  primary: {background:"#2D6EE8",color:"#fff",border:"none",borderRadius:8,padding:"9px 20px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"IBM Plex Mono"},
  green:   {background:"#00A870",color:"#fff",border:"none",borderRadius:8,padding:"9px 20px",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:"IBM Plex Mono"},
  ghost:   {background:"#F0F2FA",color:"#666",border:"1px solid #D8DCF0",borderRadius:8,padding:"9px 16px",fontSize:11,cursor:"pointer",fontFamily:"IBM Plex Mono"},
  danger:  {background:"#FFF0F0",color:"#E03030",border:"1px solid #FFD8D8",borderRadius:8,padding:"9px 16px",fontSize:11,cursor:"pointer",fontFamily:"IBM Plex Mono"},
};

// ══════════════════════════════════════════════════════════
//  VUE TRAINING
// ══════════════════════════════════════════════════════════
function TrainView({onDone}){
  const[step,setStep]     = useState("idle");
  const[pct,setPct]       = useState(0);
  const[results,setRes]   = useState([]);
  const[loadSym,setLoad]  = useState("");
  const cancelRef         = useRef(false);

  const start = useCallback(async()=>{
    setStep("loading"); cancelRef.current=false; setRes([]);

    // Grouper les intervals uniques nécessaires
    const uniqueIntervals = [...new Set(DURATIONS.map(d=>d.interval))];
    const histByInterval = {};
    let loadIdx = 0;
    const totalLoads = uniqueIntervals.length * SYMBOLS.length;

    for(const interval of uniqueIntervals){
      const days = DURATIONS.filter(d=>d.interval===interval).reduce((mx,d)=>Math.max(mx,d.days),0);
      histByInterval[interval] = {};
      for(let i=0; i<SYMBOLS.length; i++){
        if(cancelRef.current) return;
        setLoad(`${SYMBOLS[i]} (${interval})`); setPct(Math.round((loadIdx/totalLoads)*35));
        histByInterval[interval][SYMBOLS[i]] = await api.getKlines(SYMBOLS[i], interval, days);
        await new Promise(r=>setTimeout(r,80));
        loadIdx++;
      }
    }

    setStep("training"); setLoad("BACKTESTING...");
    const combos=[];
    for(const numBots of BOT_COUNTS) for(const dur of DURATIONS) for(const stake of STAKES) for(const profile of PROFILE_KEYS)
      combos.push({numBots,dur,stake,profile});
    const res=[];
    for(let i=0; i<combos.length; i++){
      if(cancelRef.current) return;
      const c=combos[i];
      const hist = histByInterval[c.dur.interval] || {};
      const bt=runBacktest({hist,numBots:c.numBots,bars:c.dur.bars,stake:c.stake,profile:c.profile});
      res.push({...c,...bt,durLabel:c.dur.label,durBars:c.dur.bars,id:`${c.numBots}-${c.dur.label}-${c.stake}-${c.profile}`});
      if(i%4===0){ setPct(Math.round(35+(i/combos.length)*65)); setRes([...res]); await new Promise(r=>setTimeout(r,0)); }
    }
    await api.saveBacktests(res);
    const best=[...res].sort((a,b)=>b.roi-a.roi)[0];
    const prof=RISK_PROFILES[best.profile];
    await api.saveBestConfig({...best,sl:prof.sl,tp:prof.tp,trailPct:prof.trailing?prof.trailing*100:null});
    setRes(res); setPct(100); setStep("done");
    onDone(res);
  },[onDone]);

  const top8=[...results].sort((a,b)=>b.roi-a.roi).slice(0,8);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16,animation:"fadeIn .4s ease"}}>
      <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"24px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
        {step==="idle"&&(
          <div style={{textAlign:"center",padding:"32px 0",display:"flex",flexDirection:"column",alignItems:"center",gap:16}}>
            <div style={{fontSize:48}}>🧠</div>
            <div style={{fontSize:16,fontWeight:700}}>Backtest multi-profils · {TOTAL_COMBOS} combinaisons</div>
            <div style={{fontSize:10,color:"#AAB0C8",lineHeight:2}}>
              <span style={{color:"#2D6EE8",fontWeight:600}}>10 symboles</span> · données 90j réelles Binance<br/>
              <span style={{color:"#00A870",fontWeight:600}}>5 profils</span> dont Tendance (trailing stop + RSI/MACD reversal)<br/>
              <span style={{color:"#E07800",fontWeight:600}}>3 mises</span> × 4 durées × 4 tailles de swarm
            </div>
            <button onClick={start} style={{...BTN.primary,padding:"12px 32px",fontSize:13,letterSpacing:1}}>⚡ LANCER LE BACKTEST</button>
          </div>
        )}
        {(step==="loading"||step==="training")&&(
          <div style={{display:"flex",alignItems:"center",gap:24}}>
            <div style={{position:"relative",width:72,height:72,flexShrink:0}}>
              <svg width="72" height="72" style={{transform:"rotate(-90deg)"}}>
                <circle cx="36" cy="36" r="30" fill="none" stroke="#E0E3F0" strokeWidth="6"/>
                <circle cx="36" cy="36" r="30" fill="none" stroke="#2D6EE8" strokeWidth="6"
                  strokeDasharray={`${(pct/100)*188.5} 188.5`} strokeLinecap="round" style={{transition:"stroke-dasharray .4s"}}/>
              </svg>
              <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:700,color:"#2D6EE8"}}>{pct}%</div>
            </div>
            <div style={{flex:1}}>
              <div style={{fontSize:12,fontWeight:600,color:"#2D6EE8",marginBottom:6,letterSpacing:1}}>
                {step==="loading"?"CHARGEMENT DONNÉES BINANCE":"BACKTESTING EN COURS"}
              </div>
              <div style={{fontSize:10,color:"#AAB0C8",marginBottom:10}}>{loadSym} — {results.length}/{TOTAL_COMBOS} combos</div>
              <div style={{height:4,background:"#F0F2FA",borderRadius:4}}>
                <div style={{width:`${pct}%`,height:"100%",background:"linear-gradient(90deg,#2D6EE8,#00A870)",borderRadius:4,transition:"width .4s"}}/>
              </div>
            </div>
          </div>
        )}
        {step==="done"&&(
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div style={{display:"flex",alignItems:"center",gap:12}}>
              <span style={{fontSize:24}}>✅</span>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:"#00A870"}}>Backtest terminé — {results.length} résultats sauvegardés</div>
                <div style={{fontSize:10,color:"#AAB0C8"}}>Va dans "Créer une simulation" pour lancer la meilleure config</div>
              </div>
            </div>
            <button onClick={start} style={BTN.ghost}>↺ Ré-entraîner</button>
          </div>
        )}
      </div>

      {top8.length>0&&(
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:8}}>
          {top8.map((r,i)=>(
            <div key={r.id} style={{background:"#fff",border:`1px solid ${i===0?"#2D6EE844":"#E0E3F0"}`,borderRadius:10,padding:"12px 14px",boxShadow:"0 2px 6px rgba(0,0,0,0.04)",animation:"fadeIn .3s ease"}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                <ProfileBadge profile={r.profile}/>
                <span style={{fontSize:10,color:i<3?"#F59E0B":"#AAB0C8"}}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}</span>
              </div>
              <div style={{fontSize:9,color:"#AAB0C8",marginBottom:6}}>{r.numBots}b · {r.stake}€ · {r.dur?.label||r.durLabel}</div>
              <div style={{fontSize:18,fontWeight:700,color:r.roi>=0?"#00A870":"#E03030"}}>{r.roi>=0?"+":""}{r.roi.toFixed(1)}%</div>
              <div style={{fontSize:9,color:"#AAB0C8"}}>{r.winRate.toFixed(0)}%WR · {r.trades} trades</div>
              {r.wfScore!=null&&<div style={{fontSize:9,marginTop:2}}>
                <span style={{background:r.wfScore>=0?"#F0FDF8":"#FFF0F0",color:r.wfScore>=0?"#00A870":"#E03030",border:`1px solid ${r.wfScore>=0?"#00A87033":"#E0303033"}`,borderRadius:3,padding:"1px 5px",fontWeight:600}}>
                  WF {r.wfScore>=0?"+":""}{r.wfScore.toFixed(1)}%
                </span>
              </div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  VUE SÉLECTION (créer une simulation depuis backtest ou perso)
// ══════════════════════════════════════════════════════════
function SelectView({backtestResults, onAdd}){
  const[selected,setSelect] = useState(null);
  const[custom,setCustom]   = useState(false); // mode config perso
  const[creating,setCreate] = useState(false);
  const[filter,setFilter]   = useState("all");
  const formRef             = useRef(null);

  // Formulaire éditable (pré-rempli depuis backtest ou vide)
  const[form,setForm] = useState({
    label:"", profile:"neutre", numBots:50, stake:10,
    durLabel:"30 jours", durBars:23, sl:4, tp:6, trailing:0,
  });

  const F = (k,v) => setForm(f=>({...f,[k]:v}));

  const applySelected = (r) => {
    const prof = RISK_PROFILES[r.profile];
    setForm({
      label:    `${prof.emoji} ${prof.label} — ${r.numBots}b × ${r.stake}€`,
      profile:  r.profile,
      numBots:  r.numBots,
      stake:    r.stake,
      durLabel: r.dur?.label||r.durLabel,
      durBars:  r.dur?.bars||r.durBars,
      sl:       +(prof.sl*100).toFixed(1),
      tp:       prof.tp ? +(prof.tp*100).toFixed(1) : 0,
      trailing: prof.trailing ? +(prof.trailing*100).toFixed(1) : 0,
    });
    setSelect(r); setCustom(true);
    setTimeout(()=>formRef.current?.scrollIntoView({behavior:"smooth",block:"start"}), 60);
  };

  const handleCreate = async()=>{
    setCreate(true);
    const sl  = form.sl/100;
    const tp  = form.trailing>0 ? null : form.tp/100;
    const trailing = form.trailing>0 ? form.trailing/100 : null;
    const prof = RISK_PROFILES[form.profile];
    await onAdd({
      label:    form.label || `${prof?.emoji||""} ${prof?.label||form.profile} — ${form.numBots}b × ${form.stake}€`,
      profile:  form.profile,
      numBots:  +form.numBots,
      stake:    +form.stake,
      sl, tp, trailing,
      durLabel:    form.durLabel,
      durBars:     +form.durBars,
      durInterval: DURATIONS.find(d=>d.label===form.durLabel)?.interval || "1h",
      roiBacktest: selected?.roi||0,
      status: "running",
    });
    setCreate(false); setSelect(null); setCustom(false);
  };

  const inputStyle = {border:"1px solid #E0E3F0",borderRadius:8,padding:"7px 10px",fontFamily:"IBM Plex Mono",fontSize:11,background:"#fff",width:"100%",color:"#1A1D2E"};
  const labelStyle = {fontSize:8,color:"#AAB0C8",letterSpacing:2,marginBottom:4,display:"block",textTransform:"uppercase"};

  const top = [...backtestResults]
    .filter(r => filter==="all" || r.profile===filter)
    .sort((a,b) => b.roi - a.roi)
    .slice(0,20);

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14,animation:"fadeIn .4s ease"}}>

      {/* Bouton simulation perso sans backtest */}
      {!custom&&(
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={()=>{setSelect(null);setCustom(true);}} style={{...BTN.primary,padding:"8px 18px",fontSize:10}}>
            ✏️ Créer une simulation personnalisée
          </button>
          {backtestResults.length===0&&<span style={{fontSize:10,color:"#AAB0C8"}}>ou lance un backtest pour sélectionner la meilleure config</span>}
        </div>
      )}

      {/* FORMULAIRE D'ÉDITION */}
      {custom&&(
        <div ref={formRef} style={{background:"#fff",border:"1px solid #2D6EE844",borderRadius:12,padding:"20px 24px",boxShadow:"0 2px 12px rgba(45,110,232,0.08)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#2D6EE8"}}>
              {selected?"✏️ MODIFIER LA CONFIG SÉLECTIONNÉE":"✏️ SIMULATION PERSONNALISÉE"}
              {selected&&<span style={{marginLeft:8,fontSize:9,color:"#AAB0C8"}}>ROI backtest : +{selected.roi.toFixed(1)}%</span>}
            </div>
            <button onClick={()=>{setCustom(false);setSelect(null);}} style={{...BTN.ghost,padding:"4px 10px",fontSize:10}}>✕ Annuler</button>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr 1fr",gap:12,marginBottom:14}}>
            {/* Nom */}
            <div style={{gridColumn:"1/-1"}}>
              <label style={labelStyle}>Nom de la simulation</label>
              <input value={form.label} onChange={e=>F("label",e.target.value)} placeholder="Ex: Test trailing agressif BTC"
                style={inputStyle}/>
            </div>

            {/* Profil */}
            <div>
              <label style={labelStyle}>Profil de risque</label>
              <select value={form.profile} onChange={e=>{
                const key=e.target.value; F("profile",key);
                const p=RISK_PROFILES[key];
                if(p){ F("sl",+(p.sl*100).toFixed(1)); F("tp",p.tp?+(p.tp*100).toFixed(1):0); F("trailing",p.trailing?+(p.trailing*100).toFixed(1):0); }
              }} style={inputStyle}>
                {PROFILE_KEYS.map(k=><option key={k} value={k}>{RISK_PROFILES[k].emoji} {RISK_PROFILES[k].label}</option>)}
              </select>
            </div>

            {/* Nb bots — aligné sur BOT_COUNTS backtestés */}
            <div>
              <label style={labelStyle}>Nombre de bots</label>
              <select value={form.numBots} onChange={e=>F("numBots",+e.target.value)} style={inputStyle}>
                {BOT_COUNTS.map(n=><option key={n} value={n}>{n} bots</option>)}
              </select>
            </div>

            {/* Mise — alignée sur STAKES backtestées */}
            <div>
              <label style={labelStyle}>Mise par bot (€)</label>
              <select value={form.stake} onChange={e=>F("stake",+e.target.value)} style={inputStyle}>
                {STAKES.map(s=><option key={s} value={s}>{s}€</option>)}
              </select>
            </div>

            {/* Durée */}
            <div>
              <label style={labelStyle}>Durée backtest ref.</label>
              <select value={form.durLabel} onChange={e=>{
                const d=DURATIONS.find(d=>d.label===e.target.value);
                if(d){F("durLabel",d.label);F("durBars",d.bars);}
              }} style={inputStyle}>
                {DURATIONS.map(d=><option key={d.label} value={d.label}>{d.label}</option>)}
              </select>
            </div>
          </div>

          {/* Paramètres risque — verrouillés sur le profil sélectionné */}
          {(()=>{
            const p = RISK_PROFILES[form.profile];
            const chips = [
              {label:"Stop Loss", value:`${(p.sl*100).toFixed(0)}%`, color:"#E03030", bg:"#FFF0F0"},
              p.trailing
                ? {label:"Trailing Stop", value:`${(p.trailing*100).toFixed(0)}%`, color:"#00A870", bg:"#F0FDF8"}
                : {label:"Take Profit",   value:`${(p.tp*100).toFixed(0)}%`,       color:"#00A870", bg:"#F0FDF8"},
            ];
            return(
              <div style={{marginBottom:16}}>
                <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,marginBottom:8,textTransform:"uppercase"}}>
                  Paramètres du profil <span style={{color:"#2D6EE8",fontWeight:700}}>{p.emoji} {p.label}</span>
                  <span style={{marginLeft:8,background:"#F0F2FA",color:"#888",border:"1px solid #D8DCF0",borderRadius:3,padding:"1px 6px",fontSize:7,fontWeight:400}}>verrouillés sur le modèle backtesté</span>
                </div>
                <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                  {chips.map(c=>(
                    <div key={c.label} style={{background:c.bg,border:`1px solid ${c.color}33`,borderRadius:8,padding:"10px 16px",display:"flex",flexDirection:"column",gap:3,minWidth:100}}>
                      <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,textTransform:"uppercase"}}>{c.label}</div>
                      <div style={{fontSize:18,fontWeight:700,color:c.color,fontFamily:"IBM Plex Mono"}}>{c.value}</div>
                    </div>
                  ))}
                  <div style={{background:"#F8F9FD",border:"1px solid #E0E3F0",borderRadius:8,padding:"10px 16px",display:"flex",flexDirection:"column",gap:3,minWidth:120}}>
                    <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,textTransform:"uppercase"}}>Capital total</div>
                    <div style={{fontSize:18,fontWeight:700,color:"#1A1D2E",fontFamily:"IBM Plex Mono"}}>{(form.numBots*form.stake).toFixed(0)}€</div>
                    <div style={{fontSize:9,color:"#AAB0C8"}}>{form.numBots} bots × {form.stake}€</div>
                  </div>
                  <div style={{background:"#F8F9FD",border:"1px solid #E0E3F0",borderRadius:8,padding:"10px 16px",display:"flex",flexDirection:"column",gap:3,minWidth:120}}>
                    <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,textTransform:"uppercase"}}>Durée de référence</div>
                    <div style={{fontSize:14,fontWeight:700,color:"#1A1D2E",fontFamily:"IBM Plex Mono"}}>{form.durLabel}</div>
                    <div style={{fontSize:9,color:"#AAB0C8"}}>{DURATIONS.find(d=>d.label===form.durLabel)?.interval||"—"} · {DURATIONS.find(d=>d.label===form.durLabel)?.bars||"—"} bougies</div>
                  </div>
                  {selected?.wfScore!=null&&(
                    <div style={{background:selected.wfScore>=0?"#F0FDF8":"#FFF0F0",border:`1px solid ${selected.wfScore>=0?"#00A87033":"#E0303033"}`,borderRadius:8,padding:"10px 16px",display:"flex",flexDirection:"column",gap:3,minWidth:100}}>
                      <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,textTransform:"uppercase"}}>WF Score</div>
                      <div style={{fontSize:18,fontWeight:700,color:selected.wfScore>=0?"#00A870":"#E03030",fontFamily:"IBM Plex Mono"}}>{selected.wfScore>=0?"+":""}{selected.wfScore.toFixed(1)}%</div>
                      <div style={{fontSize:9,color:"#AAB0C8"}}>out-of-sample</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Résumé */}
          <div style={{background:"#F8F9FD",border:"1px solid #E0E3F0",borderRadius:8,padding:"10px 14px",marginBottom:14,display:"flex",gap:20,alignItems:"center",flexWrap:"wrap"}}>
            <ProfileBadge profile={form.profile}/>
            {selected&&<span style={{fontSize:10,color:"#7B5CF5",fontWeight:600}}>ROI backtest +{selected.roi.toFixed(1)}%</span>}
            <span style={{fontSize:10,color:"#AAB0C8"}}>→ Durée réf. {form.durLabel}</span>
          </div>

          <button onClick={handleCreate} disabled={creating} style={{...BTN.green,padding:"11px 28px",fontSize:12,opacity:creating?.6:1}}>
            {creating?"Création en cours...":"🚀 Lancer la simulation"}
          </button>
        </div>
      )}

      {/* TABLE RÉSULTATS BACKTEST */}
      {backtestResults.length>0&&(
        <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
          <div style={{background:"#FAFBFE",borderBottom:"1px solid #E8EBF5",padding:"12px 18px",display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:10,fontWeight:600,color:"#1A1D2E",letterSpacing:1}}>TOP RÉSULTATS BACKTEST — cliquer pour pré-remplir</span>
            <div style={{display:"flex",gap:6,marginLeft:"auto"}}>
              {["all",...PROFILE_KEYS].map(k=>(
                <button key={k} onClick={()=>setFilter(k)} style={{...BTN.ghost,padding:"3px 9px",fontSize:9,
                  background:filter===k?"#EBF2FF":"#F0F2FA",color:filter===k?"#2D6EE8":"#666",
                  border:filter===k?"1px solid #2D6EE844":"1px solid #D8DCF0"}}>
                  {k==="all"?"Tous":RISK_PROFILES[k]?.emoji+" "+RISK_PROFILES[k]?.label}
                </button>
              ))}
            </div>
          </div>
          <div style={{maxHeight:380,overflowY:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse"}}>
              <thead>
                <tr style={{background:"#FAFBFE",position:"sticky",top:0}}>
                  {["#","Profil","Bots","Mise","Durée","ROI","WF Score","Win%","Trades","Drawdown",""].map(h=>(
                    <th key={h} style={{padding:"8px 12px",fontSize:8,color:"#AAB0C8",letterSpacing:2,textAlign:"left",fontWeight:600,borderBottom:"1px solid #E8EBF5"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {top.map((r,i)=>(
                  <tr key={r.id} onClick={()=>applySelected(r)}
                    style={{borderBottom:"1px solid #F0F2FA",cursor:"pointer",background:selected?.id===r.id?"#EFF6FF":"#fff",transition:"background .15s"}}>
                    <td style={{padding:"8px 12px",fontSize:10,color:i<3?"#F59E0B":"#AAB0C8",fontWeight:700}}>{i===0?"🥇":i===1?"🥈":i===2?"🥉":`#${i+1}`}</td>
                    <td style={{padding:"8px 12px"}}><ProfileBadge profile={r.profile}/></td>
                    <td style={{padding:"8px 12px",fontSize:11}}>{r.numBots}</td>
                    <td style={{padding:"8px 12px",fontSize:11}}>{r.stake}€</td>
                    <td style={{padding:"8px 12px",fontSize:11}}>{r.dur?.label||r.durLabel}</td>
                    <td style={{padding:"8px 12px",fontSize:13,fontWeight:700,color:r.roi>=0?"#00A870":"#E03030"}}>{r.roi>=0?"+":""}{r.roi.toFixed(1)}%</td>
                    <td style={{padding:"8px 12px",fontSize:11}}>
                      {r.wfScore!=null?<span style={{color:r.wfScore>=0?"#00A870":"#E03030",fontWeight:700}}>{r.wfScore>=0?"+":""}{r.wfScore.toFixed(1)}%</span>:<span style={{color:"#C0C4D8"}}>—</span>}
                    </td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"#E07800"}}>{r.winRate.toFixed(0)}%</td>
                    <td style={{padding:"8px 12px",fontSize:11}}>{r.trades}</td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"#E03030"}}>{r.drawdown.toFixed(1)}%</td>
                    <td style={{padding:"8px 12px"}}><button onClick={e=>{e.stopPropagation();applySelected(r);}} style={{...BTN.primary,padding:"3px 10px",fontSize:9}}>Utiliser</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Equity Curve Chart ────────────────────────────────────
function EquityCurve({equity=[], initialCapital=0}){
  if(equity.length<2) return(
    <div style={{height:140,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#C0C4D8"}}>
      Calcul de la courbe...
    </div>
  );
  const W=600, H=120, PAD=4;
  const mn=Math.min(...equity), mx=Math.max(...equity), rng=mx-mn||1;
  const pts = equity.map((v,i)=>`${((i/(equity.length-1))*(W-PAD*2)+PAD).toFixed(1)},${(H-PAD-((v-mn)/rng)*(H-PAD*2)).toFixed(1)}`);
  const path = pts.map((p,i)=>(i===0?`M${p}`:`L${p}`)).join(" ");
  const fill = path+` L${W-PAD},${H-PAD} L${PAD},${H-PAD} Z`;

  // find drawdown regions
  let peak=equity[0], ddStart=-1, maxDD=0;
  for(let i=0;i<equity.length;i++){
    if(equity[i]>=peak){ peak=equity[i]; ddStart=i; }
    const dd=(peak-equity[i])/peak;
    if(dd>maxDD) maxDD=dd;
  }

  const finalPnl = equity[equity.length-1] - initialCapital;
  const color = finalPnl>=0?"#00A870":"#E03030";

  // axis labels
  const fmt = v => v>=1000?`${(v/1000).toFixed(1)}k`:v.toFixed(1);

  return(
    <div style={{position:"relative"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <span style={{fontSize:8,color:"#AAB0C8",letterSpacing:3,textTransform:"uppercase"}}>Equity Curve — simulation backtest</span>
        <div style={{display:"flex",gap:16,alignItems:"center"}}>
          <span style={{fontSize:9,color:"#AAB0C8"}}>Max DD <span style={{color:"#E03030",fontWeight:700}}>{(maxDD*100).toFixed(1)}%</span></span>
          <span style={{fontSize:11,fontWeight:700,color:color,fontFamily:"IBM Plex Mono"}}>{finalPnl>=0?"+":""}{finalPnl.toFixed(2)}€</span>
        </div>
      </div>
      <svg width="100%" height={H+20} viewBox={`0 0 ${W} ${H+20}`} preserveAspectRatio="none" style={{overflow:"visible"}}>
        <defs>
          <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity=".25"/>
            <stop offset="100%" stopColor={color} stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="eq-line" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={color} stopOpacity=".6"/>
            <stop offset="100%" stopColor={color}/>
          </linearGradient>
        </defs>
        {/* zero / initial capital line */}
        {initialCapital>0&&mn<initialCapital&&initialCapital<mx&&(
          <line x1={PAD} y1={H-PAD-((initialCapital-mn)/rng)*(H-PAD*2)} x2={W-PAD} y2={H-PAD-((initialCapital-mn)/rng)*(H-PAD*2)}
            stroke="#AAB0C8" strokeWidth="1" strokeDasharray="4,4" opacity=".5"/>
        )}
        {/* area fill */}
        <path d={fill} fill="url(#eq-grad)"/>
        {/* line */}
        <path d={path} fill="none" stroke="url(#eq-line)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        {/* axis labels */}
        <text x={PAD} y={PAD+8} fontSize="8" fill="#AAB0C8" fontFamily="IBM Plex Mono">{fmt(mx)}€</text>
        <text x={PAD} y={H-2} fontSize="8" fill="#AAB0C8" fontFamily="IBM Plex Mono">{fmt(mn)}€</text>
        {/* start/end dots */}
        <circle cx={pts[0].split(",")[0]} cy={pts[0].split(",")[1]} r="3" fill="#AAB0C8"/>
        <circle cx={pts[pts.length-1].split(",")[0]} cy={pts[pts.length-1].split(",")[1]} r="4" fill={color}/>
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  VUE DÉTAIL simulation
// ══════════════════════════════════════════════════════════
function DetailView({sim, bots, liveHist=[], livePrices, onToggle, onDelete, onBack}){
  const pnl = sim.pnl||0;
  const pnlColor = pnl>=0?"#00A870":"#E03030";
  const prof = RISK_PROFILES[sim.profile]||{};
  const isTrailing = !!prof.trailing;

  const [equity, setEquity] = useState(null);

  useEffect(()=>{
    let cancelled = false;
    (async()=>{
      try{
        const dur = DURATIONS.find(d=>d.label===sim.durLabel)
        || DURATIONS.find(d=>d.interval===sim.durInterval)
        || DURATIONS[2];
        const hist = {};
        const botsCount = Math.min(sim.numBots||50, 15);
        await Promise.all(SYMBOLS.slice(0,botsCount).map(async s=>{
          hist[s] = await api.getKlines(s, dur.interval, dur.days);
        }));
        if(cancelled) return;
        const result = runBacktest({hist, numBots:sim.numBots||50, bars:dur.bars, stake:sim.stake||10, profile:sim.profile, withEquity:true});
        if(!cancelled) setEquity(result.equity||[]);
      }catch{ if(!cancelled) setEquity([]); }
    })();
    return ()=>{ cancelled=true; };
  },[sim._id]);

  // per-symbol P&L aggregated from bots
  const symPnl = SYMBOLS.reduce((acc,s)=>{
    const symBots = bots.filter(b=>b.symbol===s);
    acc[s] = { pnl:symBots.reduce((t,b)=>t+b.pnl,0), inPos:symBots.some(b=>b.inPos), count:symBots.length };
    return acc;
  },{});

  // live chart path
  const livePath=()=>{
    const vals=(liveHist||[]).filter(v=>!isNaN(v));
    if(vals.length<2) return {path:"",fill:""};
    const W2=600,H2=80,mn=Math.min(...vals,0),mx=Math.max(...vals,0.001),rng=mx-mn||.01;
    const pts=vals.map((v,i)=>`${((i/(vals.length-1))*W2).toFixed(1)},${(H2-((v-mn)/rng)*H2).toFixed(1)}`);
    const p=pts.map((pt,i)=>(i===0?`M${pt}`:`L${pt}`)).join(" ");
    return{path:p, fill:`${p} L${W2},${H2} L0,${H2} Z`};
  };
  const {path:lp,fill:lf}=livePath();

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14,animation:"fadeIn .4s ease"}}>
      <style>{`
        @keyframes pulseDot{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.5;transform:scale(1.4)}}
        @keyframes tickerUp{from{color:#AAB0C8}to{color:#00A870}}
        @keyframes tickerDn{from{color:#AAB0C8}to{color:#E03030}}
        .pnl-live-val{transition:color .3s,transform .2s;display:inline-block}
      `}</style>

      {/* TOPBAR */}
      <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"18px 22px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <button onClick={onBack} style={{...BTN.ghost,padding:"6px 12px",fontSize:11}}>← Retour</button>
          <span style={{fontSize:15,fontWeight:700}}>{sim.label}</span>
          <ProfileBadge profile={sim.profile}/>
          {isTrailing&&<span style={{fontSize:9,background:"#F0FDF8",color:"#00A870",border:"1px solid #00A87033",borderRadius:4,padding:"2px 8px"}}>📈 TRAILING STOP</span>}
        </div>
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>onToggle(sim)} style={sim.status==="running"?BTN.ghost:BTN.green}>
            {sim.status==="running"?"⏸ Pause":"▶ Reprendre"}
          </button>
          <button onClick={()=>onDelete(sim)} style={BTN.danger}>🗑 Supprimer</button>
        </div>
      </div>

      {/* LIVE P&L HERO */}
      <div style={{background:"#fff",border:`1px solid ${pnlColor}33`,borderRadius:12,padding:"22px 26px",boxShadow:`0 4px 20px ${pnlColor}18`,position:"relative",overflow:"hidden"}}>
        {/* background pulse */}
        <div style={{position:"absolute",inset:0,background:`radial-gradient(ellipse at 10% 50%, ${pnlColor}08 0%, transparent 70%)`,pointerEvents:"none"}}/>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:24}}>

          {/* ticker principal */}
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              {sim.status==="running"&&(
                <span style={{width:8,height:8,borderRadius:"50%",background:"#00A870",display:"inline-block",animation:"pulseDot 1.4s ease infinite"}}/>
              )}
              <span style={{fontSize:9,color:"#AAB0C8",letterSpacing:3,textTransform:"uppercase"}}>
                {sim.status==="running"?"P&L LIVE — Session en cours":"P&L SESSION"}
              </span>
            </div>
            <div style={{display:"flex",alignItems:"baseline",gap:6}}>
              <span className="pnl-live-val" key={Math.round(pnl*100)} style={{fontSize:40,fontWeight:700,color:pnlColor,fontFamily:"IBM Plex Mono",letterSpacing:-1}}>
                {pnl>=0?"+":""}{pnl.toFixed(4)}
              </span>
              <span style={{fontSize:20,color:pnlColor,fontWeight:300}}>€</span>
            </div>
            <div style={{display:"flex",gap:20,marginTop:2}}>
              {[
                ["ROI session", `${((sim.numBots*sim.stake)>0?(pnl/(sim.numBots*sim.stake)*100):0).toFixed(2)}%`],
                ["Win rate",    `${(sim.winRate||0).toFixed(1)}%`],
                ["Trades",      `${sim.trades||0}`],
                ["Capital",     `${(sim.numBots*sim.stake).toFixed(0)}€`],
              ].map(([k,v])=>(
                <div key={k}><div style={{fontSize:8,color:"#AAB0C8",marginBottom:1}}>{k}</div><div style={{fontSize:12,fontWeight:600,fontFamily:"IBM Plex Mono"}}>{v}</div></div>
              ))}
            </div>
          </div>

          {/* mini live chart */}
          <div style={{flex:1,minWidth:0}}>
            {liveHist.length>=2?(
              <svg width="100%" height={90} viewBox="0 0 600 80" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="lpg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={pnlColor} stopOpacity=".25"/>
                    <stop offset="100%" stopColor={pnlColor} stopOpacity="0"/>
                  </linearGradient>
                </defs>
                <line x1="0" y1="80" x2="600" y2="80" stroke="#F0F2FA" strokeWidth="1"/>
                <path d={lf} fill="url(#lpg)"/>
                <path d={lp} fill="none" stroke={pnlColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            ):(
              <div style={{height:90,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#C0C4D8"}}>
                {sim.status==="running"?"En attente de signaux Binance...":"Simulation en pause"}
              </div>
            )}
          </div>

          {/* ROI backtest */}
          <div style={{textAlign:"right",flexShrink:0}}>
            <div style={{fontSize:8,color:"#AAB0C8",letterSpacing:2,marginBottom:4}}>ROI BACKTEST</div>
            <div style={{fontSize:22,fontWeight:700,color:"#7B5CF5",fontFamily:"IBM Plex Mono"}}>{sim.roiBacktest?`+${sim.roiBacktest.toFixed(1)}%`:"—"}</div>
            <div style={{fontSize:8,color:"#AAB0C8",marginTop:4}}>{sim.durLabel||"—"}</div>
          </div>
        </div>
      </div>

      {/* EQUITY CURVE */}
      <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"18px 22px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
        <EquityCurve equity={equity||[]} initialCapital={(sim.numBots||50)*(sim.stake||10)}/>
      </div>

      {/* LIVE PAR SYMBOLE */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 220px",gap:14}}>
        <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"18px 22px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
          <div style={{fontSize:9,color:"#AAB0C8",letterSpacing:3,marginBottom:14,textTransform:"uppercase"}}>P&L par symbole — live</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:8}}>
            {SYMBOLS.map(s=>{
              const d=symPnl[s]; if(!d||d.count===0) return null;
              const c=d.pnl>0?"#00A870":d.pnl<0?"#E03030":"#AAB0C8";
              return(
                <div key={s} style={{background:d.inPos?`${c}10`:"#F8F9FD",border:`1px solid ${d.inPos?c+"44":"#E0E3F0"}`,borderRadius:8,padding:"10px 12px",textAlign:"center",transition:"background .3s"}}>
                  <div style={{fontSize:9,fontWeight:700,color:"#1A1D2E",marginBottom:3}}>{s.replace("USDT","")}</div>
                  <div style={{fontSize:13,fontWeight:700,color:c,fontFamily:"IBM Plex Mono"}}>{d.pnl>=0?"+":""}{d.pnl.toFixed(3)}</div>
                  <div style={{fontSize:8,color:d.inPos?"#2D6EE8":"#AAB0C8",marginTop:2}}>{d.inPos?"● IN":"○ WAIT"}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"16px 18px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
          <div style={{fontSize:9,color:"#AAB0C8",letterSpacing:3,marginBottom:10,textTransform:"uppercase"}}>Prix live</div>
          {SYMBOLS.slice(0,8).map(s=>{
            const price=livePrices[s];
            return(
              <div key={s} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:"1px solid #F0F2FA"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  {symPnl[s]?.inPos&&<span style={{width:5,height:5,borderRadius:"50%",background:"#2D6EE8",display:"inline-block",animation:"pulseDot 1.4s ease infinite"}}/>}
                  <span style={{fontSize:10,color:"#666"}}>{s.replace("USDT","")}</span>
                </div>
                <span style={{fontSize:11,fontWeight:600,color:price?"#1A1D2E":"#C0C4D8",fontFamily:"IBM Plex Mono"}}>
                  {price?`$${price.toLocaleString(undefined,{maximumFractionDigits:2})}`:"···"}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,overflow:"hidden",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
        <div style={{background:"#FAFBFE",borderBottom:"1px solid #E8EBF5",padding:"12px 18px"}}>
          <span style={{fontSize:9,fontWeight:600,color:"#1A1D2E",letterSpacing:2,textTransform:"uppercase"}}>Bots ({bots.length})</span>
        </div>
        <div style={{maxHeight:320,overflowY:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead>
              <tr style={{background:"#FAFBFE",position:"sticky",top:0}}>
                {["Paire","P&L","Capital","Trades","Win%","Signal","Statut",...(isTrailing?["📈 Sommet","🎯 Trailing"]:[""])].map((h,i)=>(
                  <th key={i} style={{padding:"8px 12px",fontSize:8,color:"#AAB0C8",letterSpacing:2,textAlign:"left",fontWeight:600,borderBottom:"1px solid #E8EBF5"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bots.map(bot=>{
                const trailLevel = isTrailing&&bot.highWater>0 ? bot.highWater*(1-prof.trailing) : null;
                return(
                  <tr key={bot.id} style={{borderBottom:"1px solid #F0F2FA",background:bot.status==="IN"?"#F5F8FF":"#fff"}}>
                    <td style={{padding:"8px 12px",fontWeight:700,fontSize:12}}>{bot.symbol.replace("USDT","")}</td>
                    <td style={{padding:"8px 12px",fontWeight:700,fontSize:12,color:bot.pnl>=0?"#00A870":"#E03030"}}>{bot.pnl>=0?"+":""}{bot.pnl.toFixed(4)}€</td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"#888"}}>{bot.capital.toFixed(3)}€</td>
                    <td style={{padding:"8px 12px",fontSize:11}}>{bot.trades}</td>
                    <td style={{padding:"8px 12px",fontSize:11,color:"#E07800"}}>{bot.trades>0?`${(bot.wins/bot.trades*100).toFixed(0)}%`:"—"}</td>
                    <td style={{padding:"8px 12px"}}><SigBadge sig={bot.lastSignal||"HOLD"}/></td>
                    <td style={{padding:"8px 12px",fontSize:11,color:bot.status==="IN"?"#2D6EE8":"#AAB0C8",fontWeight:600}}>{bot.status==="IN"?"● IN":"○ WAIT"}</td>
                    {isTrailing&&<>
                      <td style={{padding:"8px 12px",fontSize:11,color:"#00A870",fontWeight:600}}>
                        {bot.inPos&&bot.highWater>0?`$${bot.highWater.toLocaleString(undefined,{maximumFractionDigits:2})}`:"—"}
                      </td>
                      <td style={{padding:"8px 12px"}}>
                        {trailLevel&&bot.inPos?<span style={{background:"#FFF5EB",color:"#E07800",border:"1px solid #E0780033",borderRadius:4,padding:"2px 6px",fontSize:10,fontWeight:700}}>
                          ${trailLevel.toLocaleString(undefined,{maximumFractionDigits:2})}
                        </span>:<span style={{color:"#C0C4D8"}}>—</span>}
                      </td>
                    </>}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  VUE DASHBOARD (liste des simulations)
// ══════════════════════════════════════════════════════════
function DashboardView({sims, activeBots, liveHistories, livePrices, onToggle, onDelete, onDetail}){
  if(sims.length===0) return(
    <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"56px",textAlign:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
      <div style={{fontSize:48,marginBottom:12}}>📊</div>
      <div style={{fontSize:14,fontWeight:600,marginBottom:8}}>Aucune simulation active</div>
      <div style={{fontSize:10,color:"#AAB0C8"}}>Lance un backtest puis crée une simulation pour commencer</div>
    </div>
  );

  return(
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(340px,1fr))",gap:14,animation:"fadeIn .4s ease"}}>
      {sims.map(sim=>{
        const pnl=sim.pnl||0;
        const pnlColor=pnl>=0?"#00A870":"#E03030";
        const hist=liveHistories[sim._id]||[];
        const bots=activeBots[sim._id]||[];
        const active=bots.filter(b=>b.status==="IN").length;
        return(
          <div key={sim._id} style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"18px 20px",boxShadow:"0 2px 8px rgba(0,0,0,0.05)",cursor:"pointer",transition:"box-shadow .2s"}}
            onClick={()=>onDetail(sim)} onMouseOver={e=>e.currentTarget.style.boxShadow="0 4px 16px rgba(45,110,232,0.12)"}
            onMouseOut={e=>e.currentTarget.style.boxShadow="0 2px 8px rgba(0,0,0,0.05)"}>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:0}}>
                <ProfileBadge profile={sim.profile}/>
                <span style={{fontSize:11,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{sim.label}</span>
              </div>
              <div style={{display:"flex",gap:4,flexShrink:0}} onClick={e=>e.stopPropagation()}>
                <button onClick={()=>onToggle(sim)} style={{...BTN.ghost,padding:"4px 10px",fontSize:9}}>
                  {sim.status==="running"?"⏸":"▶"}
                </button>
                <button onClick={()=>onDelete(sim)} style={{...BTN.danger,padding:"4px 10px",fontSize:9}}>🗑</button>
              </div>
            </div>
            <div style={{display:"flex",alignItems:"flex-end",justifyContent:"space-between",marginBottom:10}}>
              <div>
                <div style={{fontSize:9,color:"#AAB0C8",marginBottom:2}}>P&L LIVE</div>
                <div style={{fontSize:22,fontWeight:700,color:pnlColor,fontFamily:"IBM Plex Mono"}}>{pnl>=0?"+":""}{pnl.toFixed(3)}€</div>
              </div>
              <MiniSpark data={hist} color={pnlColor}/>
            </div>
            <div style={{display:"flex",gap:16,borderTop:"1px solid #F0F2FA",paddingTop:10}}>
              {[
                ["Bots actifs",`${active}/${bots.length}`],
                ["Win rate",`${(sim.winRate||0).toFixed(0)}%`],
                ["Trades",sim.trades||0],
                ["Mise",`${sim.stake}€×${sim.numBots}`],
              ].map(([k,v])=>(
                <div key={k}><div style={{fontSize:8,color:"#AAB0C8",marginBottom:1}}>{k}</div><div style={{fontSize:11,fontWeight:600}}>{v}</div></div>
              ))}
              <div style={{marginLeft:"auto"}}>
                <span style={{fontSize:8,background:sim.status==="running"?"#F0FDF8":"#F8F9FD",color:sim.status==="running"?"#00A870":"#AAB0C8",border:`1px solid ${sim.status==="running"?"#00A87033":"#E0E3F0"}`,borderRadius:10,padding:"2px 8px"}}>
                  {sim.status==="running"?"● LIVE":"⏸ PAUSE"}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ══════════════════════════════════════════════════════════
export default function SwarmOptimizer(){
  const[view,setView]           = useState("sims");
  const[health,setHealth]       = useState(null);
  const[btResults,setBTRes]     = useState([]);
  const[sims,setSims]           = useState([]);
  const[activeBots,setActBots]  = useState({});
  const[liveHistories,setLiveH] = useState({});
  const[livePrices,setLivePx]   = useState({});
  const[wsStatus,setWsStatus]   = useState("disconnected");
  const[detailSim,setDetailSim] = useState(null);

  const wsRef      = useRef(null);
  const candlesRef = useRef({});
  const botsRef    = useRef({});
  const simsRef    = useRef([]);
  const snapTick   = useRef({});
  simsRef.current  = sims;

  // ── Init ─────────────────────────────────────────────
  useEffect(()=>{(async()=>{
    const [h, dbSims, dbBT] = await Promise.all([api.health(), api.getSims(), api.loadLatestBacktests()]);
    setHealth(h);
    if(dbBT?.length>0) setBTRes(dbBT.map(r=>({...r,dur:{label:r.durLabel,bars:r.durBars},id:`${r.numBots}-${r.durLabel}-${r.stake}-${r.profile}`})));
    if(dbSims?.length>0){
      setSims(dbSims);
      const running=dbSims.filter(s=>s.status==="running");
      if(running.length>0){ await initBots(running); startWS(running); }
    }
  })();},[]);

  // ── Init bots ────────────────────────────────────────
  const initBots = useCallback(async(simList)=>{
    if(!candlesRef.current["BTCUSDT"]){
      await Promise.all(SYMBOLS.map(async s=>{
        const klines = await api.getKlines(s,"1m",1);
        candlesRef.current[s] = klines.slice(-100);
      }));
    }
    const nb = {...botsRef.current};
    simList.forEach(sim=>{
      if(nb[sim._id]) return;
      nb[sim._id] = Array.from({length:Math.min(sim.numBots, SYMBOLS.length*3)},(_,i)=>({
        id:i, symbol:SYMBOLS[i%SYMBOLS.length],
        capital:sim.stake, initialCapital:sim.stake,
        inPos:false, entry:0, highWater:0, prevRSI:50, prevMACD:0,
        trades:0, wins:0, pnl:0,
        sl:sim.sl, tp:sim.tp||null,
        status:"WAIT", lastSignal:"HOLD", openedAt:null,
      }));
    });
    botsRef.current = nb;
    setActBots({...nb});
  },[]);

  // ── WebSocket ────────────────────────────────────────
  const startWS = useCallback((runSims)=>{
    if(wsRef.current) wsRef.current.close();
    if(!runSims?.length) return;
    setWsStatus("connecting");
    const streams = SYMBOLS.map(s=>`${s.toLowerCase()}@kline_1m`).join("/");
    const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
    ws.onopen  = ()=>setWsStatus("live");
    ws.onerror = ()=>setWsStatus("error");
    ws.onclose = ()=>setWsStatus("disconnected");
    ws.onmessage = (ev)=>{
      try{
        const {data:msg} = JSON.parse(ev.data); if(!msg?.k) return;
        const k=msg.k, sym=k.s, price=+k.c;
        setLivePx(p=>({...p,[sym]:price}));
        if(k.x && candlesRef.current[sym]){
          candlesRef.current[sym].push({close:price,open:+k.o,high:+k.h,low:+k.l,vol:+k.v});
          if(candlesRef.current[sym].length>200) candlesRef.current[sym].shift();
        }
        const bars   = candlesRef.current[sym]||[];
        const signal = getSignal(bars);
        const running = simsRef.current.filter(s=>s.status==="running");
        const nb = {...botsRef.current};
        running.forEach(sim=>{
          if(!nb[sim._id]) return;
          const prof = RISK_PROFILES[sim.profile]||{};
          const isTrailing = !!prof.trailing;
          nb[sim._id] = nb[sim._id].map(bot=>{
            if(bot.symbol!==sym) return bot;
            let {capital,inPos,entry,trades,wins,openedAt,highWater=0,prevRSI=50,prevMACD=0} = bot;
            const closes2 = bars.map(b=>b.close);
            const rsi  = calcRSI(closes2);
            const macd = calcEMA(closes2,12) - calcEMA(closes2,26);
            if(!inPos && signal==="BUY"){
              inPos=true; entry=price; highWater=price; openedAt=new Date(); capital*=.999;
            } else if(inPos){
              if(price>highWater) highWater=price;
              const hitSL    = price <= entry*(1-bot.sl);
              const hitTP    = bot.tp && price >= entry*(1+bot.tp);
              const hitTrail = isTrailing && price <= highWater*(1-prof.trailing);
              const rsiRev   = isTrailing && prevRSI>=70 && rsi<65;
              const macdRev  = isTrailing && prevMACD>=0 && macd<0;
              if(hitSL||hitTP||hitTrail||rsiRev||macdRev){
                const chg=(price-entry)/entry;
                capital+=capital*chg; capital*=.999;
                const win=price>entry; if(win) wins++;
                trades++;
                const reason=hitSL?"STOP_LOSS":hitTP?"TAKE_PROFIT":hitTrail?"TRAILING_STOP":rsiRev?"RSI_REVERSAL":"MACD_REVERSAL";
                api.saveTrade({botId:bot.id,symbol:sym,simId:sim._id,side:win?"WIN":"LOSS",entryPrice:entry,exitPrice:price,pnl:capital-bot.initialCapital,stake:bot.initialCapital,profile:sim.profile,openedAt,closedAt:new Date(),reason});
                inPos=false; entry=0; highWater=0; openedAt=null;
              }
            }
            return{...bot,capital,inPos,entry,trades,wins,highWater,prevRSI:rsi,prevMACD:macd,status:inPos?"IN":"WAIT",lastSignal:signal,openedAt,pnl:capital-bot.initialCapital};
          });
          const bots=nb[sim._id];
          const totalPnl=bots.reduce((s,b)=>s+b.pnl,0);
          const trades=bots.reduce((s,b)=>s+b.trades,0);
          const wins=bots.reduce((s,b)=>s+b.wins,0);
          const winRate=trades>0?(wins/trades)*100:0;
          setSims(prev=>prev.map(s=>s._id===sim._id?{...s,pnl:totalPnl,trades,winRate}:s));
          setLiveH(prev=>({...prev,[sim._id]:[...(prev[sim._id]||[]).slice(-120),totalPnl]}));
          if(!snapTick.current[sim._id]) snapTick.current[sim._id]=0;
          if(++snapTick.current[sim._id]%30===0)
            api.updateSim(sim._id,{pnl:totalPnl,trades,winRate});
        });
        botsRef.current=nb;
        setActBots({...nb});
      }catch{}
    };
    wsRef.current=ws;
  },[]);

  // ── Handlers ─────────────────────────────────────────
  const handleAdd = useCallback(async(data)=>{
    const created = await api.createSim(data);
    if(!created) return;
    setSims(prev=>[created,...prev]);
    await initBots([created]);
    startWS([...simsRef.current.filter(s=>s.status==="running"),created]);
    setView("sims");
  },[initBots,startWS]);

  const handleToggle = useCallback(async(sim)=>{
    const ns = sim.status==="running"?"paused":"running";
    await api.updateSim(sim._id,{status:ns});
    setSims(prev=>prev.map(s=>s._id===sim._id?{...s,status:ns}:s));
    const updated = simsRef.current.map(s=>s._id===sim._id?{...s,status:ns}:s);
    const running = updated.filter(s=>s.status==="running");
    if(running.length>0) startWS(running);
    else{ wsRef.current?.close(); setWsStatus("disconnected"); }
  },[startWS]);

  const handleDelete = useCallback(async(sim)=>{
    if(!window.confirm(`Supprimer "${sim.label}" ?`)) return;
    await api.deleteSim(sim._id);
    setSims(prev=>prev.filter(s=>s._id!==sim._id));
    const nb={...botsRef.current}; delete nb[sim._id];
    botsRef.current=nb; setActBots({...nb});
    if(detailSim?._id===sim._id){ setDetailSim(null); setView("sims"); }
    const running=simsRef.current.filter(s=>s._id!==sim._id&&s.status==="running");
    if(running.length>0) startWS(running);
    else{ wsRef.current?.close(); setWsStatus("disconnected"); }
  },[detailSim,startWS]);

  useEffect(()=>()=>{ wsRef.current?.close(); },[]);

  // ── Tabs ─────────────────────────────────────────────
  const tabs = [
    ["sims",  `📊 Simulations (${sims.length})`],
    ["select","➕ Créer une simulation"],
    ["train", "🧠 Backtest"],
  ];

  return(
    <div style={{fontFamily:"IBM Plex Mono, monospace",color:"#1A1D2E",display:"flex",flexDirection:"column",gap:14}}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* TOPBAR */}
      <div style={{background:"#fff",border:"1px solid #E0E3F0",borderRadius:12,padding:"12px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",boxShadow:"0 2px 8px rgba(0,0,0,0.05)"}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {tabs.map(([k,l])=>(
            <button key={k} onClick={()=>{setDetailSim(null);setView(k);}} style={{...BTN.ghost,
              background:view===k&&!detailSim?"#EBF2FF":"#F0F2FA",
              color:view===k&&!detailSim?"#2D6EE8":"#666",
              border:view===k&&!detailSim?"1px solid #2D6EE844":"1px solid #D8DCF0",
              padding:"7px 16px",fontSize:10}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {health&&(
            <span style={{fontSize:8,background:health.mongo==="connected"?"#F0FDF8":"#FFF0F0",border:`1px solid ${health.mongo==="connected"?"#00A87033":"#E0303033"}`,borderRadius:10,padding:"3px 10px",color:health.mongo==="connected"?"#00A870":"#E03030"}}>
              ● MONGO · {health.collections?.backtests||0} backtests · {health.collections?.trades||0} trades
            </span>
          )}
          {wsStatus==="live"&&(
            <span style={{fontSize:8,background:"#F0FDF8",border:"1px solid #00A87033",borderRadius:10,padding:"3px 10px",color:"#00A870"}}>
              ● BINANCE LIVE
            </span>
          )}
        </div>
      </div>

      {/* CONTENU */}
      {detailSim ? (
        <DetailView
          sim={sims.find(s=>s._id===detailSim._id)||detailSim}
          bots={activeBots[detailSim._id]||[]}
          liveHist={liveHistories[detailSim._id]||[]}
          livePrices={livePrices}
          onToggle={handleToggle}
          onDelete={handleDelete}
          onBack={()=>setDetailSim(null)}
        />
      ) : view==="sims" ? (
        <DashboardView sims={sims} activeBots={activeBots} liveHistories={liveHistories} livePrices={livePrices} onToggle={handleToggle} onDelete={handleDelete} onDetail={sim=>{setDetailSim(sim);}}/>
      ) : view==="select" ? (
        <SelectView backtestResults={btResults} onAdd={handleAdd}/>
      ) : (
        <TrainView onDone={res=>{ setBTRes(res); setView("select"); }}/>
      )}
    </div>
  );
}
